import { Injectable } from '@angular/core';
import {
  FormValueObject,
  ISampleDataRow,
  ISampleTableColumnVisibility,
  RepeatSamplesAcrossLanesState,
  IRunContentSettingsField
} from '@app/run-planning/interface';
import _, { cloneDeep, every, get, groupBy, intersection, isEmpty, map, range, union, uniq } from 'lodash';
import { SequencingRunIndexInfo } from '@stratus/gss-ng-sdk/api/models/sequencing-run-index-info';
import { IndexStrategy } from '@stratus/gss-ng-sdk';
import { InstrumentPlatformInfo } from '@root/node_modules/@stratus/gss-ng-sdk/api/models/instrument-platform-info';
import { ColumnHeaderTooltip, InteractiveTableColDef } from '@app/cloud-run-prep/run-setup/interactive-table/InteractiveTableColDef.interface';
import { FREE_TEXT_MAX_LENGTH, InstrumentAgentType, REGEX_VALIDATIONS, SUPPORTED_HIDDEN_UI_FIELDS } from '@app/run-planning/constants';
import { IndexAdapterKit } from '@app/run-planning/model/index-adapter-kit';
import { RowNode } from 'ag-grid-community';
import { AnalysisVersionDefinition, SampleSettingsConfigurationLevel } from '@app/run-planning/model/analysis-version-definition';
import { SubSampleDefinition } from '@stratus/gss-ng-sdk/api/models/sub-sample-definition';
import { SubSamplesConfiguration } from '@stratus/gss-ng-sdk/api/models/sub-samples-configuration';
import { OverrideCycles } from '@app/run-planning/model/override-cycles';
import { isMultiSelectField } from '@app/run-planning/model/form-field';
import { SubSampleNameUtility } from '@app/run-planning/services/sample-table/sub-sample-name-utility';
import { CodeFeaturesService } from '@app/core/services/user/code-features.service';
import { ConfigSetupState } from '@app/run-planning/config-setup/config-setup.state';

enum ErrorText {
  SAMPLE_NAME_REQUIRED = 'Sample ID is required and can contain alphanumeric characters, hyphens, underscores with maximum length of 100.',
  INDEX_SEQUENCE_REQUIRED = 'Required. Only the characters A, C, G, and T are allowed.',
  DUPLICATE_INDEX = 'Index is already used in one or more lanes. Please make another selection.',
  FIELD_PATTERN = 'Field can only contain alphanumeric characters, hyphens, and underscores.',
}

// "Default" columns for sample data table
export enum ColumnName {
  // For multi-lane
  LANE_NUMBERS = 'laneNumbers',
  SAMPLE_NAME = 'sampleName',
  INDEX_1_SEQUENCE = 'index1Sequence',
  INDEX_2_SEQUENCE = 'index2Sequence',
  INDEX_1_NAME = 'index1Name',
  INDEX_2_NAME = 'index2Name',
  INDEX_CONTAINER_POSITION = 'indexContainerPosition',
  BARCODE_MISMATCH_READ_1 = 'barcodeMismatchRead1',
  BARCODE_MISMATCH_READ_2 = 'barcodeMismatchRead2',
  PROJECT = 'projectName'
}

// Extra columns that have special handling logic
export enum ExtraColumnName {
  SAMPLE_PROJECT = 'Sample_Project'
}

export enum DownloadTemplateOption {
  EXTERNAL_STORAGE = 'ExternalStorage',
  LOCAL_STORAGE = 'LocalStorage'
}

export enum ImportSamplesOption {
  CSV = 'Csv',
  SAMPLE_SHEET = 'SampleSheet',
  EXTERNAL_STORAGE = 'ExternalStorage',
  LOCAL_STORAGE = 'LocalStorage',
  BSSH = 'Bssh'
}

const HAS_ANY_SPACE_PATTERN = /\s/g;
const HAS_LEADING_OR_TRAILING_SPACES_PATTERN = /^\s|\s$/;

enum CellTransformType {
  NONE = 0,
  TRIM_SPACES = 1,
  REMOVE_ALL_SPACES = 2
}

const BARCODE_MISMATCH_READ_HEADER_TOOLTIP: ColumnHeaderTooltip = {
  content: 'Why is allowing mismatches when demultiplexing desirable?',
  href: 'https://support.illumina.com/bulletins/2021/08/why-is-allowing-mismatches-when-demultiplexing-desirable-.html',
  // ColumnHeaderTooltip.show will be determined by showHyperlinkInTooltip
}

@Injectable({
  providedIn: 'root'
})
export class SampleTableService {

  constructor(private codeFeaturesService: CodeFeaturesService) { }

  public updateConfigSampleTableState(config: ConfigSetupState,
    isMultiAnalysis: boolean, hasAct: boolean, supportMultiLane: boolean
  ) {
    config.sampleTableColumnVisibility = this.getColumnVisibility(
      isMultiAnalysis, hasAct, null, null, config.enableRepeatSamplesAcrossLanes, supportMultiLane, null, config.hiddenUiFields);
    config.columnDefinitions = this.getDefaultColDefs();
  }
  
  public getColumnVisibility(
    isMultiAnalysis: boolean, hasAct: boolean, indexInfo?: SequencingRunIndexInfo,
    indexStrategy?: IndexStrategy, enableRepeatSamplesAcrossLanes: RepeatSamplesAcrossLanesState = RepeatSamplesAcrossLanesState.HIDDEN,
    supportMultiLane?: boolean, currentVisibility?: ISampleTableColumnVisibility, hiddenUiFields?: string[]): ISampleTableColumnVisibility {

    let showIndex1Column = currentVisibility ? currentVisibility.showIndex1Column : true;
    let showIndex2Column = currentVisibility ? currentVisibility.showIndex2Column : true;

    if (indexInfo && !isEmpty(indexInfo) && !every(indexInfo, _.isNil)) {
      showIndex1Column = indexInfo.hasIndex1;
      showIndex2Column = indexInfo.hasIndex2;
    } else if (indexStrategy) {
      if (indexStrategy === IndexStrategy.NO_INDEX) {
        showIndex1Column = false;
        showIndex2Column = false;
      } else if (indexStrategy === IndexStrategy.SINGLE) {
        showIndex1Column = true;
        showIndex2Column = false;
      } else {
        showIndex1Column = true;
        showIndex2Column = true;
      }
    }
    let showBarcodeIndex1Column, showBarcodeIndex2Column;

    if (!isMultiAnalysis) {
      showBarcodeIndex1Column = showBarcodeIndex2Column = false;
    } else {
      // barcode mismatch column is dependent on index column visibility
      showBarcodeIndex1Column = showIndex1Column;
      showBarcodeIndex2Column = showIndex2Column;

      if (hiddenUiFields) {
        // Check if specified in hidden ui fields
        if (showBarcodeIndex1Column && hiddenUiFields.includes(SUPPORTED_HIDDEN_UI_FIELDS.barcodeMismatchRead1)) {
          showBarcodeIndex1Column = false;
        }
        if (showBarcodeIndex2Column && hiddenUiFields.includes(SUPPORTED_HIDDEN_UI_FIELDS.barcodeMismatchRead2)) {
          showBarcodeIndex2Column = false;
        }
      }
    }

    return {
      ...currentVisibility,
      showIndex1Column,
      showIndex2Column,
      showBarcodeIndex1Column,
      showBarcodeIndex2Column,
      disableLanes: hasAct || false,
      disableSampleName: hasAct || false,
      disableWellPosition: hasAct || false,
      disableIndex1Column: hasAct || false,
      disableIndex2Column: hasAct || false,
      hideLanes: this.shouldLaneColumnBeHidden(enableRepeatSamplesAcrossLanes, supportMultiLane, currentVisibility),
    };
  }

  public getColumnDefinitions(
    indexAdapterKit: IndexAdapterKit, columnVisibility: ISampleTableColumnVisibility,
    instrumentPlatformInfo: InstrumentPlatformInfo, avd: AnalysisVersionDefinition,
    isStandAlone: boolean, customSettingsFields: string[], overrideCycles: OverrideCycles,
    index1: any, index2: any, runContentSettings: any): InteractiveTableColDef[] {
    let colDefs: InteractiveTableColDef[];
    if (indexAdapterKit && !indexAdapterKit.isUnspecified) {
      colDefs = this.getDefaultColDefs(indexAdapterKit, columnVisibility, instrumentPlatformInfo.maxNumberOfLanes);
    } else {
      colDefs = this.getUnspecifiedColDefs(columnVisibility, instrumentPlatformInfo.maxNumberOfLanes);
    }
    if (avd && avd.hasSubSamples) {
      const subSamplesConfig = avd.settings.subSamplesConfiguration;

      const assocField = avd.analysisSampleSettings.fields.find(x => x.id === subSamplesConfig.associationFieldId);
      if (!assocField) {
        throw new Error(`Association field ${subSamplesConfig.associationFieldId} was not found in avd ${avd.id}`);
      }

      const colDefsFromSampleTable = this.expandColumnDefinitions(colDefs, subSamplesConfig.subSampleDefinitions,
        indexAdapterKit && indexAdapterKit.hasFixedLayout, indexAdapterKit && indexAdapterKit.isUnspecified
      );
      const colDefsFromSampleSettings = this.createColumnDefinitionsFromSampleSettings(avd);
      colDefs = this.combineColDefs(colDefsFromSampleTable, colDefsFromSampleSettings, subSamplesConfig);

      // Update index sequence column length limitation based on override cycles
      for(const field of subSamplesConfig.subSampleDefinitions) {
        const fieldIdPrefix = field.id;
        colDefs = this.updateColumnDefinitionsWithOverrideCycles(colDefs, overrideCycles, Number(index1), Number(index2), fieldIdPrefix);
      }
    } else {
      colDefs = this.updateColumnDefinitionsWithOverrideCycles(colDefs, overrideCycles, Number(index1), Number(index2));
    }
    colDefs = this.updateColumnDefinitionsWithRunContentSettings(colDefs, runContentSettings);
    colDefs = this.updateColumnDefinitionsWithCustomSettings(colDefs, customSettingsFields);
    colDefs = this.addProjectColumnWhenShouldBeVisible(colDefs, isStandAlone, get(avd, 'settings.hiddenUiFields', []));

    return colDefs;
  }

  private defaultColDefForCellTypes: { [key: string]: InteractiveTableColDef } = {
    'textbox': {
      minWidth: 140,
      width: 140,
      cellParams: {
        type: 'text',
        placeholder: '',
        required: false,
        maxLength: FREE_TEXT_MAX_LENGTH
      },
      enableHeaderFillDown: true,
      fillDownFn: SampleTableService.defaultFillDown,
      enableHeaderSort: true
    },
    'select': {
      width: 140,
      cellParams: {
        type: 'dropdown',
        placeholder: 'Select',
        required: false
      },
      enableHeaderFillDown: true,
      fillDownFn: SampleTableService.defaultFillDown,
      enableHeaderSort: false
    }
  };

  private updateColumnDefinitionsWithOverrideCycles(colDefs: InteractiveTableColDef[], overrideCycles: OverrideCycles,
    index1: number, index2: number, fieldIdPrefix = null): InteractiveTableColDef[] {
    const i1ColDef = colDefs.find(colDef => colDef.field === (fieldIdPrefix ? `${fieldIdPrefix}_${ColumnName.INDEX_1_SEQUENCE}` : ColumnName.INDEX_1_SEQUENCE));
    const i2ColDef = colDefs.find(colDef => colDef.field === (fieldIdPrefix ? `${fieldIdPrefix}_${ColumnName.INDEX_2_SEQUENCE}` : ColumnName.INDEX_2_SEQUENCE));
      // If overrideCycles is not set, set length limit from index 1 & 2
      // If overrideCycles is set, set length limit from overrideCycles
    const wrappedOverrideCycles = OverrideCycles.wrap(overrideCycles);
    const index1Length = (wrappedOverrideCycles && wrappedOverrideCycles.haveValue()) ? wrappedOverrideCycles.unmaskedIndex1Length() : index1;
    const index2Length = (wrappedOverrideCycles && wrappedOverrideCycles.haveValue()) ? wrappedOverrideCycles.unmaskedIndex2Length() : index2;

    if (i1ColDef && i1ColDef.cellParams) {
      i1ColDef.cellParams.length = index1Length;
    }
    if (i2ColDef && i2ColDef.cellParams) {
      i2ColDef.cellParams.length = index2Length;
    }
    return colDefs;
  }

  private getRunContentSettingsFields(allRunContentSettings: any, onlyVisibleFields = false): IRunContentSettingsField[] {
    const result: IRunContentSettingsField[] = [];
    if (allRunContentSettings && allRunContentSettings.fields) {
      const visibleFields = allRunContentSettings.fields.filter(field => onlyVisibleFields ? !field.hidden : true);
      for (const rawField of visibleFields) {
        const { id, label, type, required, hidden } = rawField;
        const field: IRunContentSettingsField = { id, label, type, required, hidden };
        result.push(field);
      }
    }
    return result;
  }
  
  private updateColumnDefinitionsWithRunContentSettings(colDefs: InteractiveTableColDef[], allRunContentSettings: any): InteractiveTableColDef[] {
    const runContentSettingsFields = this.getRunContentSettingsFields(allRunContentSettings);

    for (const field of runContentSettingsFields) {
      // get default column def for this field type
      const colDef = (field.type && this.defaultColDefForCellTypes[field.type]) ? cloneDeep(this.defaultColDefForCellTypes[field.type]) : null;

      if (colDef) {
        // complete the column def
        colDef.field = field.id;
        colDef.headerName = colDef.headerTooltip = field.label ? field.label : field.id;
        colDef.cellParams.required = field.required;

        // if field is hidden, hide the column by default (but allow user to unhide it)
        colDef.hideByDefault = field.hidden;

        colDefs.push(colDef);
      } else {
        console.error(`Cannot find default column definition for runContentSettingsField of type '${field.type}'.`)
      }
    }

    return colDefs;
  }

  private updateColumnDefinitionsWithCustomSettings(colDefs: InteractiveTableColDef[], customSettingsFields: string[]): InteractiveTableColDef[] {
    // remove existing custom columns
    const result = colDefs.filter(colDef => !colDef.field.startsWith('Custom_'));

    if (customSettingsFields) {
      for (const field of customSettingsFields) {
        const colDef = cloneDeep(this.defaultColDefForCellTypes.textbox);
        colDef.field = colDef.headerName = colDef.headerTooltip = field;
        colDef.width = this.getColumnWidth(colDef, field);

        result.push(colDef);
      }
    }

    return result;
  }

  private addProjectColumnWhenShouldBeVisible(colDefs: InteractiveTableColDef[],
    isStandAlone?: boolean, hiddenUiFields: string[] = []): InteractiveTableColDef[] {
    const projectColDef: InteractiveTableColDef = {
      headerName: 'Project',
      headerTooltip: 'Project',
      field: ColumnName.PROJECT,
      minWidth: 140,
      width: 140,
      cellParams: {
        type: 'text',
        placeholder: '',
        required: false,
        maxLength: FREE_TEXT_MAX_LENGTH
      },
      enableHeaderFillDown: true,
      fillDownFn: SampleTableService.defaultFillDown,
      enableHeaderSort: true
    };

    // In standalone mode, project column should not even be in the colDef (i.e. not even available in column switcher to unhide)
    if (isStandAlone) {
      return colDefs;
    }

    // Originally, AVD that needs to display Sample Project will have Project in its hiddenUiFields
    // However because now we need to display Sample Project for other configs as well,
    // we will just have a special logic to hide Project column by default as long as Sample Project field.hidden = false.
    // And this special logic is guarded by the sampleProjectMutuallyExclusive code feature.
    const sampleProjectColDef = colDefs.find(colDef => colDef.field === ExtraColumnName.SAMPLE_PROJECT);
    projectColDef.hideByDefault = hiddenUiFields.includes(SUPPORTED_HIDDEN_UI_FIELDS.project)
      || (this.codeFeaturesService.sampleProjectMutuallyExclusiveEnabled
            && sampleProjectColDef && !sampleProjectColDef.hideByDefault);
    colDefs.push(projectColDef);
    return colDefs;
  }

  /**
   * Combine column definitions from sample table and sample-settings table for TSO, and sort them in this order:
   *   1. "Association field"
   *   2. Sub-sample-specific fields, i.e.  DNA xxxx, RNA xxx
   *   3. Non sub-sample-specific fields
   */
  // TODO investigate the implementation below to make it clearer and/or more efficient
  private combineColDefs(colDefsFromSampleTable: InteractiveTableColDef[], colDefsFromSampleSettings: InteractiveTableColDef[],
    subSamplesConfig: SubSamplesConfiguration): InteractiveTableColDef[] {
    const FIRST_GROUP = '!'; // can use any character outside of the domain of sub-sample
    const LAST_GROUP = '~';

    let colDefs = colDefsFromSampleTable.concat(colDefsFromSampleSettings);
    const groupedColDefs = groupBy(colDefs, c => {
      if (c.field === subSamplesConfig.associationFieldId) {
        return FIRST_GROUP;
      } else {
        const subSample = subSamplesConfig.subSampleDefinitions.find(s => c.field.startsWith(`${s.id}_`));
        return subSample ? subSample.id : LAST_GROUP;
      }
    });

    const result = groupedColDefs[FIRST_GROUP];
    for (const def of subSamplesConfig.subSampleDefinitions) {
      if (!isEmpty(groupedColDefs[def.id])) {
        result.push(...groupedColDefs[def.id]);
      }
    }
    if (!isEmpty(groupedColDefs[LAST_GROUP])) {
      result.push(...groupedColDefs[LAST_GROUP]);
    }
    return result;
  }

  static defaultFillDown(params, val) {
    const results = [];
    let prefix = '';
    let counter = 1;
    for (let i = val.length - 1; i >= 0; i--) {
      if (isNaN(parseInt(val[i], 10))) {
        break;
      } else {
        counter = parseInt(val.substr(i), 10);
        prefix = val.substr(0, i);
      }
    }
    params.api.forEachNode(node => {
      const data = node.data;
      data[params.column.getColId()] = `${prefix}${counter}`;
      results.push(data);
      counter++;
    });
    return results;
  }

  static barcodeMismatchFillDown(params, val) {
    const results = [];
    params.api.forEachNode(node => {
      const data = node.data;
      // for barcode mismatch index column, copy first value when filling down
      if (val) {
        // if barcode mismatch and value is empty, do not change data
        data[params.column.getColId()] = val;
        results.push(data);
      }
    });
    return results;
  }

  private getUnspecifiedColDefs(columnVisibility: ISampleTableColumnVisibility, maxNumberOfLanes: number): InteractiveTableColDef[] {

    const lanesColDef: InteractiveTableColDef = {
      headerName: 'Lanes',
      headerTooltip: 'Lanes',
      field: ColumnName.LANE_NUMBERS,
      minWidth: 100,
      width: 100,
      cellParams: {
        type: 'multiselect',
        values: [], // to be updated with range of lanes in getDefaultColDefs
        required: true
      },
      errorText: {
        required: 'Required.',
        duplicate: ErrorText.DUPLICATE_INDEX
      },
      enableHeaderFillDown: true,
      fillDownFn: SampleTableService.defaultFillDown
    };

    const sampleNameColDef: InteractiveTableColDef = {
      headerName: 'Sample ID',
      headerTooltip: 'Sample ID',
      field: ColumnName.SAMPLE_NAME,
      width: 310,
      cellParams: {
        type: 'text',
        regex: REGEX_VALIDATIONS.restrictedNameRegex,
        placeholder: 'Enter Sample ID...',
        required: true
      },
      errorText: {
        required: ErrorText.SAMPLE_NAME_REQUIRED,
        pattern: ErrorText.SAMPLE_NAME_REQUIRED
      },
      enableHeaderFillDown: true,
      fillDownFn: SampleTableService.defaultFillDown,
      enableHeaderSort: true,
      actAsId: true
    };

    const i7SeqColDef: InteractiveTableColDef = {
      headerName: 'Index 1',
      headerTooltip: 'Index 1',
      field: ColumnName.INDEX_1_SEQUENCE,
      width: 300,
      hide: false,
      cellParams: {
        type: 'text',
        required: true,
        regex: REGEX_VALIDATIONS.sequenceRegex,
        length: null
      },
      errorText: {
        required: ErrorText.INDEX_SEQUENCE_REQUIRED,
        pattern: ErrorText.INDEX_SEQUENCE_REQUIRED,
        length: 'Index 1 is ${0} characters long. It must be ${1} characters long.',
        duplicate: 'This lane and index combination is already used. Make another selection'
      }
    };

    const i5SeqColDef: InteractiveTableColDef = {
      headerName: 'Index 2',
      headerTooltip: 'Index 2',
      field: ColumnName.INDEX_2_SEQUENCE,
      width: 300,
      hide: false,
      cellParams: {
        type: 'text',
        regex: REGEX_VALIDATIONS.sequenceRegex,
        required: true,
        length: null
      },
      errorText: {
        required: ErrorText.INDEX_SEQUENCE_REQUIRED,
        pattern: ErrorText.INDEX_SEQUENCE_REQUIRED,
        length: 'Index 2 is ${0} characters long. It must be ${1} characters long.',
        duplicate: 'This lane and index combination is already used. Make another selection'
      }
    };

    const barcodeIndex1ColDef: InteractiveTableColDef = {
      headerName: `Barcode Mismatches Index 1`,
      headerTooltip: 'Barcode Mismatches Index 1',
      field: ColumnName.BARCODE_MISMATCH_READ_1,
      width: 200,
      errorText: {
        required: 'Required.'
      },
      cellParams: {
        type: 'dropdown',
        placeholder: 'Select',
        required: true,
        values: [0, 1, 2]
      },
      headerClass: 'break-word-header',
      defaultValue: 1,
      enableHeaderFillDown: true,
      fillDownFn: SampleTableService.barcodeMismatchFillDown,
      customHeaderTooltip: BARCODE_MISMATCH_READ_HEADER_TOOLTIP
    };

    const barcodeIndex2ColDef: InteractiveTableColDef = {
      headerName: `Barcode Mismatches Index 2`,
      headerTooltip: 'Barcode Mismatches Index 2',
      field: ColumnName.BARCODE_MISMATCH_READ_2,
      width: 200,
      errorText: {
        required: 'Required.'
      },
      cellParams: {
        type: 'dropdown',
        placeholder: 'Select',
        required: true,
        values: [0, 1, 2]
      },
      headerClass: 'break-word-header',
      defaultValue: 1,
      enableHeaderFillDown: true,
      fillDownFn: SampleTableService.barcodeMismatchFillDown,
      customHeaderTooltip: BARCODE_MISMATCH_READ_HEADER_TOOLTIP
    };

    const defs = [
      lanesColDef,
      sampleNameColDef,
      i7SeqColDef,
      i5SeqColDef,
    ];

    i7SeqColDef.hide = !columnVisibility.showIndex1Column;
    i5SeqColDef.hide = !columnVisibility.showIndex2Column;

    if (columnVisibility.showBarcodeIndex1Column) {
      defs.push(barcodeIndex1ColDef)
    }
    if (columnVisibility.showBarcodeIndex2Column) {
      defs.push(barcodeIndex2ColDef);
    }

    this.showHideLaneColumn(defs, columnVisibility, maxNumberOfLanes);

    // Disable columns
    sampleNameColDef.editable = !columnVisibility.disableSampleName && sampleNameColDef.editable;
    i7SeqColDef.editable = !columnVisibility.disableIndex1Column && i7SeqColDef.editable;
    i5SeqColDef.editable = !columnVisibility.disableIndex2Column && i5SeqColDef.editable;

    i7SeqColDef.computed = i5SeqColDef.computed = false;
    return defs;
  }

  // TODO To investigate refactoring this logic to make it clearer and not duplicated
  private createColumnDefinitionsFromSampleSettings(avd: AnalysisVersionDefinition): InteractiveTableColDef[] {
    const result: InteractiveTableColDef[] = [];
    const subSamplesDefs = avd.settings.subSamplesConfiguration.subSampleDefinitions;

    for (const field of avd.analysisSampleSettings.fields) {
      if (field.configurationLevel && (field.configurationLevel === SampleSettingsConfigurationLevel.LOGICAL
        || field.configurationLevel === SampleSettingsConfigurationLevel.PHYSICAL)) {
        // If field is logical or physical, shouldn't appear at sub-sample level
        continue;
      }

      const settings = field.settings;

      const supportedSubSampleIds = get(settings, 'supportedSubSampleIds', []);
      const supportedSubSampleDefs = subSamplesDefs.filter(x => supportedSubSampleIds.includes(x.id));

      if (!isEmpty(supportedSubSampleDefs)) {
        for (const subSamplesDef of supportedSubSampleDefs) {
          const def = {} as InteractiveTableColDef;
          def.headerName = def.headerTooltip = `${subSamplesDef.labelPrefix}${field.label}`;
          def.field = `${subSamplesDef.id}_${field.id}`;

          const defaultValue = avd.getSampleSettingFieldDefaultValue(field, subSamplesDef.id);
          if (!isEmpty(defaultValue)) {
            def.defaultValue = defaultValue;
          }

          def.hide = field.hidden;
          def.width = this.getColumnWidth(def, field.id);
          def.cellParams = {
            type: this.mapGssFieldTypeToAgGrid(field),
            required: field.required
          };

          if ((field.type === 'select') && field.choices) {
            def.cellParams.values = field.choices.map(x => x.value);
            if (isMultiSelectField(field)) {
              def.cellParams.settings = field.settings;
            }
          }

          def.errorText = {
            required: def.cellParams.required ? 'Field is required' : undefined,
            pattern: def.cellParams.regex ? 'Invalid value' : undefined
          };

          if (field.helpText) {
            def.customHeaderTooltip = {
              content: field.helpText,
              show: true
            }
          }

          result.push(def);
        }
      } else {
        const def = {} as InteractiveTableColDef;
        def.headerName = def.headerTooltip = field.label;
        def.field = field.id;

        const defaultValue = avd.getSampleSettingFieldDefaultValue(field);
        if (!isEmpty(defaultValue)) {
          def.defaultValue = defaultValue;
        }

        def.hide = field.hidden;
        def.width = this.getColumnWidth(def, field.id);
        def.cellParams = {
          type: this.mapGssFieldTypeToAgGrid(field),
          required: field.required
        };

        if ((field.type === 'select') && field.choices) {
          def.cellParams.values = field.choices.map(x => x.value);
          if (isMultiSelectField(field)) {
            def.cellParams.settings = field.settings;
          }
        }

        def.errorText = {
          required: def.cellParams.required ? 'Field is required' : undefined,
          pattern: def.cellParams.regex ? 'Invalid value' : undefined
        };

        if (field.helpText) {
          def.customHeaderTooltip = {
            content: field.helpText,
            show: true
          }
        }

        result.push(def);
      }
    }

    // assocField will be used to generate actual sample names, so the validation should follow Sample Name validation.
    const assocFieldColDef = result.find(x => x.field === avd.settings.subSamplesConfiguration.associationFieldId);
    if (assocFieldColDef) {
      assocFieldColDef.cellParams.regex = REGEX_VALIDATIONS.restrictedNameRegex;
      assocFieldColDef.errorText.pattern = ErrorText.FIELD_PATTERN;
      assocFieldColDef.errorText.duplicate = assocFieldColDef.headerName + ' is used in multiple rows.';
      assocFieldColDef.actAsId = true;
    }

    return result;
  }

  /**
   * Creating a new set of column-definitions based on existing column-definition and sub-sample configuration.
   */
  private expandColumnDefinitions(colDefs: InteractiveTableColDef[], subSamplesDefs: Array<SubSampleDefinition>, hasFixedLayout: boolean,
    isUnspecified: boolean
  ): InteractiveTableColDef[] {
    const result: InteractiveTableColDef[] = [];

    // all others are assumed as sub-sample-specific
    const otherColumns = colDefs.filter(x => (x.field !== ColumnName.SAMPLE_NAME));
    for (const subSamplesDef of subSamplesDefs) {
      for (const col of otherColumns) {
        const newColDef = cloneDeep(col);
        newColDef.headerName = newColDef.headerTooltip = `${subSamplesDef.labelPrefix}${col.headerName}`;
        newColDef.width = this.getColumnWidth(newColDef, col.field);
        newColDef.field = `${subSamplesDef.id}_${col.field}`;
        delete newColDef.minWidth; // allow unrestricted resize for TSO mode

        newColDef.cellParams.required = false;
        if (col.cellParams.required && (col.field !== ColumnName.INDEX_CONTAINER_POSITION)) {
          if (hasFixedLayout) {
            newColDef.cellParams.requiredIf = `${subSamplesDef.id}_${ColumnName.INDEX_CONTAINER_POSITION}`;
          } else if(isUnspecified) {
            newColDef.cellParams.requiredIf = `${subSamplesDef.id}_${ColumnName.INDEX_1_SEQUENCE}`;
          } else {
            newColDef.cellParams.requiredIf = `${subSamplesDef.id}_${ColumnName.INDEX_1_NAME}`;
          }
        }

        if ((col.field === ColumnName.INDEX_1_NAME) || (col.field === ColumnName.INDEX_2_NAME)) {
          const wellPositionColDef = colDefs.find(x => x.field === ColumnName.INDEX_CONTAINER_POSITION);
          if (wellPositionColDef && !wellPositionColDef.hide) {
            this.adjustIndexNameLookup(newColDef, subSamplesDef);
            newColDef.hideByDefault = true;
          } else {
            newColDef.hideByDefault = false;
          }
        } else if (col.field === ColumnName.INDEX_1_SEQUENCE) {
          if (!isUnspecified) {
            this.adjustI7SeqLookup(newColDef, subSamplesDef);
            newColDef.hideByDefault = true;
          } else {
            // If the kit is unspecified, sequences should come directly from user input. Remove the custom value getter that currently pulls values from the index column
            delete newColDef.valueGetter;
          }
        } else if (col.field === ColumnName.INDEX_2_SEQUENCE) {
          if (!isUnspecified) {
            this.adjustI5SeqLookup(newColDef, subSamplesDef);
            newColDef.hideByDefault = true;
          } else {
            // If the kit is unspecified, sequences should come directly from user input. Remove the custom value getter that currently pulls values from the index column
            delete newColDef.valueGetter;
          }
        } else if (col.field === ColumnName.BARCODE_MISMATCH_READ_1 || col.field === ColumnName.BARCODE_MISMATCH_READ_2) {
          newColDef.hideByDefault = true;
        }

        result.push(newColDef);
      }
    }

    return result;
  }

  public getDefaultColDefs(indexAdapterKit?: IndexAdapterKit, columnVisibility?: ISampleTableColumnVisibility,
    maxNumberOfLanes?: number) {

    const lanesColDef: InteractiveTableColDef = {
      headerName: 'Lanes',
      headerTooltip: 'Lanes',
      field: ColumnName.LANE_NUMBERS,
      minWidth: 90,
      width: 90,
      cellParams: {
        type: 'multiselect',
        values: [], // to be updated with range of lanes in getDefaultColDefs
        required: true
      },
      errorText: {
        required: 'Required.',
        duplicate: ErrorText.DUPLICATE_INDEX
      },
      fillDownFn: SampleTableService.defaultFillDown,
      enableHeaderFillDown: true
    };

    const sampleNameColDef: InteractiveTableColDef = {
      headerName: 'Sample ID',
      headerTooltip: 'Sample ID',
      field: ColumnName.SAMPLE_NAME,
      width: 200,
      cellParams: {
        type: 'text',
        regex: REGEX_VALIDATIONS.restrictedNameRegex,
        required: true
      },
      errorText: {
        required: ErrorText.SAMPLE_NAME_REQUIRED,
        pattern: ErrorText.SAMPLE_NAME_REQUIRED
      },
      enableHeaderFillDown: true,
      fillDownFn: SampleTableService.defaultFillDown,
      enableHeaderSort: true,
      actAsId: true
    };

    const wellPosColDef: InteractiveTableColDef = {
      headerName: 'Well Position',
      headerTooltip: 'Well Position',
      field: ColumnName.INDEX_CONTAINER_POSITION,
      width: 130,
      hide: true,
      errorText: {
        required: 'Required.',
        duplicate: 'This well position is already used. Make another selection'
      },
      cellParams: {
        type: 'dropdown',
        placeholder: 'Select',
        values: [],
        required: true
      }
    };

    const i7NameColDef: InteractiveTableColDef = {
      headerName: 'I7 index',
      headerTooltip: 'I7 index',
      field: ColumnName.INDEX_1_NAME,
      width: 130,
      errorText: {
        required: 'Required.',
        duplicate: 'This index is already used. Make another selection.',
      },
      cellParams: {
        type: 'dropdown',
        placeholder: 'Select',
        required: true,
        values: []
      }
    };

    const i5NameColDef: InteractiveTableColDef = {
      headerName: 'I5 index',
      headerTooltip: 'I5 index',
      field: ColumnName.INDEX_2_NAME,
      width: 130,
      errorText: {
        required: 'Required.',
        duplicate: 'This index is already used. Make another selection.'
      },
      cellParams: {
        type: 'dropdown',
        placeholder: 'Select',
        required: true,
        values: []
      }
    };

    const i7SeqColDef: InteractiveTableColDef = {
      headerName: 'Index 1',
      headerTooltip: 'Index 1',
      field: ColumnName.INDEX_1_SEQUENCE,
      width: 145,
      editable: false,
      cellParams: {
        type: 'text',
        required: true,
        regex: REGEX_VALIDATIONS.sequenceRegex,
        values: []
      },
      errorText: {
        required: ErrorText.INDEX_SEQUENCE_REQUIRED,
        pattern: ErrorText.INDEX_SEQUENCE_REQUIRED
      }
    };

    const i5SeqColDef: InteractiveTableColDef = {
      headerName: 'Index 2',
      headerTooltip: 'Index 2',
      field: ColumnName.INDEX_2_SEQUENCE,
      width: 145,
      suppressAutoSize: true,
      editable: false,
      errorText: {
        required: ErrorText.INDEX_SEQUENCE_REQUIRED,
        pattern: ErrorText.INDEX_SEQUENCE_REQUIRED
      },
      cellParams: {
        type: 'text',
        regex: REGEX_VALIDATIONS.sequenceRegex,
        required: true
      }
    };

    const barcodeIndex1ColDef: InteractiveTableColDef = {
      headerName: `Barcode Mismatches Index 1`,
      field: ColumnName.BARCODE_MISMATCH_READ_1,
      headerTooltip: `Barcode Mismatches Index 1`,
      errorText: {
        required: 'Required.'
      },
      width: 130,
      minWidth: 90,
      suppressAutoSize: true,
      cellParams: {
        type: 'dropdown',
        placeholder: 'Select',
        required: true,
        values: [0, 1, 2]
      },
      headerClass: 'break-word-header',
      defaultValue: 1,
      enableHeaderFillDown: true,
      fillDownFn: SampleTableService.barcodeMismatchFillDown,
      customHeaderTooltip: BARCODE_MISMATCH_READ_HEADER_TOOLTIP
    };

    const barcodeIndex2ColDef: InteractiveTableColDef = {
      headerName: `Barcode Mismatches Index 2`,
      field: ColumnName.BARCODE_MISMATCH_READ_2,
      headerTooltip: `Barcode Mismatches Index 2`,
      errorText: {
        required: 'Required.'
      },
      width: 130,
      minWidth: 90,
      suppressAutoSize: true,
      cellParams: {
        type: 'dropdown',
        placeholder: 'Select',
        required: true,
        values: [0, 1, 2]
      },
      headerClass: 'break-word-header',
      defaultValue: 1,
      enableHeaderFillDown: true,
      fillDownFn: SampleTableService.barcodeMismatchFillDown,
      customHeaderTooltip: BARCODE_MISMATCH_READ_HEADER_TOOLTIP
    };

    const defs: InteractiveTableColDef[] = [
      lanesColDef,
      sampleNameColDef,
      wellPosColDef,
      i7NameColDef,
      i7SeqColDef,
      i5NameColDef,
      i5SeqColDef
    ];

    if (columnVisibility) {
      i7NameColDef.hide = i7SeqColDef.hide = !columnVisibility.showIndex1Column;
      i5NameColDef.hide = i5SeqColDef.hide = !columnVisibility.showIndex2Column;
      if (columnVisibility.showBarcodeIndex1Column) {
        defs.push(barcodeIndex1ColDef);
      }
      if (columnVisibility.showBarcodeIndex2Column) {
        defs.push(barcodeIndex2ColDef);
      }
    }

    if (maxNumberOfLanes) {
      this.showHideLaneColumn(defs, columnVisibility, maxNumberOfLanes);
    }

    if (indexAdapterKit) {
      const wellPosOptions = this.getWellPosOptions(indexAdapterKit);
      wellPosColDef.cellParams.values = wellPosOptions;
      wellPosColDef.hide = wellPosOptions.length <= 0;
      i7NameColDef.editable = i5NameColDef.editable = wellPosOptions.length <= 0;

      i7NameColDef.cellParams.values = this.getI7NameOptions(indexAdapterKit);
      i5NameColDef.cellParams.values = this.getI5NameOptions(indexAdapterKit);
    }

    if (columnVisibility) {
      wellPosColDef.hide = wellPosColDef.hide || (!columnVisibility.showIndex1Column && !columnVisibility.showIndex2Column);
    }

    // Disable columns
    if (lanesColDef && columnVisibility) {
      lanesColDef.editable = !columnVisibility.disableLanes && lanesColDef.editable;
    }

    if (columnVisibility) {
      sampleNameColDef.editable = !columnVisibility.disableSampleName && sampleNameColDef.editable;
      wellPosColDef.editable = !columnVisibility.disableWellPosition && wellPosColDef.editable;
      i7NameColDef.editable = !columnVisibility.disableIndex1Column && i7NameColDef.editable;
      i5NameColDef.editable = !columnVisibility.disableIndex2Column && i5NameColDef.editable;
    }

    // check whether need to rename header from "well position" to "index ID"
    if (indexAdapterKit && indexAdapterKit.settings && indexAdapterKit.settings.fixedLayoutPositionKeyByIndexId) {
      wellPosColDef.headerName = 'Index ID';
      wellPosColDef.headerTooltip = 'Index ID';
      wellPosColDef.errorText = {
        required: 'Required.',
        duplicate: 'This index ID is already used. Make another selection'
      };

    } else {
      wellPosColDef.headerName = 'Well Position';
      wellPosColDef.headerTooltip = 'Well Position';
      wellPosColDef.errorText = {
        required: 'Required.',
        duplicate: 'This well position is already used. Make another selection'
      };
    }

    if (i7NameColDef) {
      i7NameColDef.computed = wellPosColDef && !wellPosColDef.hide;
    }

    if (i5NameColDef) {
      i5NameColDef.computed = wellPosColDef && !wellPosColDef.hide;
    }

    i7SeqColDef.computed = i7NameColDef && !i7NameColDef.hide;
    i5SeqColDef.computed = i5NameColDef && !i5NameColDef.hide;

    if (i7NameColDef.computed && indexAdapterKit) {
      this.setupI7NameLookup(i7NameColDef, indexAdapterKit);
    } else {
      this.clearLookup(i7NameColDef);
    }

    if (i5NameColDef.computed && indexAdapterKit) {
      this.setupI5NameLookup(i5NameColDef, indexAdapterKit);
    } else {
      this.clearLookup(i5NameColDef);
    }

    if (i7SeqColDef.computed && indexAdapterKit) {
      this.setupI7SeqLookup(i7SeqColDef, indexAdapterKit);
    } else {
      this.clearLookup(i7SeqColDef);
    }

    if (i5SeqColDef.computed && indexAdapterKit) {
      this.setupI5SeqLookup(i5SeqColDef, indexAdapterKit);
    } else {
      this.clearLookup(i5SeqColDef);
    }

    return defs;
  }

  public maxColumnValueLength(colDef: InteractiveTableColDef, data: ISampleDataRow[]): number {
    if (!colDef || !data) {
      return undefined;
    }

    const length = _.chain(data)
      .filter(x => x[colDef.field])
      .map(x => x[colDef.field].length)
      .max()
      .value() || 0;

    return length;
  }

  public transformCellValue(cellValue: any, column: string, node?: RowNode) {
    let cellTransformType;
    switch (column) {
      case ColumnName.INDEX_1_SEQUENCE:
      case ColumnName.INDEX_2_SEQUENCE:
        cellTransformType = HAS_ANY_SPACE_PATTERN.test(cellValue as string) ? CellTransformType.REMOVE_ALL_SPACES : CellTransformType.NONE;
        break;
      case ColumnName.SAMPLE_NAME:
        cellTransformType = HAS_LEADING_OR_TRAILING_SPACES_PATTERN.test(cellValue as string)
          ? CellTransformType.TRIM_SPACES : CellTransformType.NONE;
        break;
      default:
        cellTransformType = CellTransformType.NONE;
    }

    let newCellValue;
    switch (cellTransformType) {
      case CellTransformType.REMOVE_ALL_SPACES:
        newCellValue = cellValue.replace(HAS_ANY_SPACE_PATTERN, '');
        break;
      case CellTransformType.TRIM_SPACES:
        newCellValue = cellValue.trim();
        break;
      default:
        return cellValue;
    }

    if (node) {
      node.setDataValue(column, newCellValue);
    }
    return newCellValue;
  }

  private shouldLaneColumnBeHidden(enableRepeatSamplesAcrossLanes: RepeatSamplesAcrossLanesState,
    supportMultiLane: boolean, columnVisibility: ISampleTableColumnVisibility): boolean {
    if (enableRepeatSamplesAcrossLanes !== RepeatSamplesAcrossLanesState.HIDDEN) {
      switch (enableRepeatSamplesAcrossLanes) {
        case RepeatSamplesAcrossLanesState.CHECKED:
          return true;
        case RepeatSamplesAcrossLanesState.UNCHECKED:
          return false;
        default:
          break;
      }
    }
    if (supportMultiLane != null) {
      return !supportMultiLane;
    }
    return get(columnVisibility, 'hideLanes', false);
  }

  private showHideLaneColumn(defs: InteractiveTableColDef[], columnVisibility: ISampleTableColumnVisibility, maxNumberOfLanes: number) {
    const laneColDef = defs.find(x => x.field === ColumnName.LANE_NUMBERS);
    laneColDef.hide = columnVisibility.hideLanes;
    if (!laneColDef.hide) {
      const laneValues = range(1, maxNumberOfLanes + 1);
      laneColDef.cellParams.values = map(laneValues, laneNum => laneNum.toString());  // cast to string to prevent invalid paste
    }
  }

  private getI7NameOptions(indexAdapterKit: IndexAdapterKit): string[] {
    if (!indexAdapterKit || !indexAdapterKit.indexSequences) {
      return [];
    }

    return indexAdapterKit.indexSequences
      .filter((obj) => obj.readNumber === 1)
      .map((v) => v.name);
  }

  private getI5NameOptions(indexAdapterKit: IndexAdapterKit): string[] {
    if (!indexAdapterKit || !indexAdapterKit.indexSequences) {
      return [];
    }

    return indexAdapterKit.indexSequences
      .filter((obj) => obj.readNumber === 2)
      .map((v) => v.name);
  }

  private getWellPosOptions(indexAdapterKit: IndexAdapterKit): string[] {
    const fixedIndexPositions = get(indexAdapterKit, 'settings.fixedIndexPositions', null);
    if (!fixedIndexPositions) {
      return [];
    }
    return map(fixedIndexPositions, (x: string) => x.split('/')[0]);
  }

  private clearLookup(colDef: InteractiveTableColDef) {
    colDef.valueGetter = undefined;
    if (colDef.data) {
      colDef.data.lookup = undefined;
    }
  }

  private setupI7NameLookup(colDef: InteractiveTableColDef, indexAdapterKit: IndexAdapterKit) {
    const i7Lookup = {};
    for (const item of indexAdapterKit.settings.fixedIndexPositions) {
      const re = /([^\/]+)\/(\w+)(-(\w+))?/g;
      const match = re.exec(item);
      if (match) {
        i7Lookup[match[1]] = match[2];
      } else {
        console.error(`Error creating i7Lookup for ${item}. Unable to extract index name and I7 sequence from ${item}.`);
      }
    }
    colDef.data = colDef.data || {};
    colDef.data.lookup = i7Lookup;

    colDef.valueGetter = params =>
      (params.colDef as InteractiveTableColDef).data.lookup[params.data[ColumnName.INDEX_CONTAINER_POSITION]];
  }

  private setupI5NameLookup(colDef: InteractiveTableColDef, indexAdapterKit: IndexAdapterKit) {
    const i5Lookup = {};
    for (const item of indexAdapterKit.settings.fixedIndexPositions) {
      const re = /([^\/]+)\/(\w+)-(\w+)/g;
      const match = re.exec(item);
      if (match) {
        i5Lookup[match[1]] = match[3];
      } else {
        console.error(`Error creating i5Lookup for ${item}. Unable to extract index name and I5 sequence from ${item}.`);
      }
    }
    colDef.data = colDef.data || {};
    colDef.data.lookup = i5Lookup;

    colDef.valueGetter = params =>
      (params.colDef as InteractiveTableColDef).data.lookup[params.data[ColumnName.INDEX_CONTAINER_POSITION]];
  }

  private adjustIndexNameLookup(colDef: InteractiveTableColDef, subSampleDef: SubSampleDefinition) {
    colDef.valueGetter = params => {
      const key = `${subSampleDef.id}_${ColumnName.INDEX_CONTAINER_POSITION}`;
      const lookupData = get(params.colDef, 'data.lookup', {});
      return lookupData[params.data[key]];
    };
  }

  private setupI7SeqLookup(colDef: InteractiveTableColDef, indexAdapterKit: IndexAdapterKit) {
    const i7Lookup = {};
    for (const item of indexAdapterKit.indexSequences) {
      i7Lookup[`${item.name}|${item.readNumber}`] = item.sequence;
    }
    colDef.data = colDef.data || {};
    colDef.data.lookup = i7Lookup;

    colDef.valueGetter = params => {
      const i7Name = params.getValue(ColumnName.INDEX_1_NAME);
      const lookupData = get(params.colDef, 'data.lookup', {});
      return lookupData[`${i7Name}|1`];
    };
  }

  private adjustI7SeqLookup(colDef: InteractiveTableColDef, subSampleDef: SubSampleDefinition) {
    colDef.valueGetter = params => {
      const i7Name = params.getValue(`${subSampleDef.id}_${ColumnName.INDEX_1_NAME}`);
      const lookupData = get(params.colDef, 'data.lookup', {});
      return lookupData[`${i7Name}|1`];
    };
  }

  private setupI5SeqLookup(colDef: InteractiveTableColDef, indexAdapterKit: IndexAdapterKit) {
    const i5Lookup = {};
    for (const item of indexAdapterKit.indexSequences) {
      i5Lookup[`${item.name}|${item.readNumber}`] = item.sequence;
    }
    colDef.data = colDef.data || {};
    colDef.data.lookup = i5Lookup;

    colDef.valueGetter = params => {
      const i5Name = params.getValue(ColumnName.INDEX_2_NAME);
      const iColDef = params.colDef as InteractiveTableColDef;
      return iColDef.data.lookup[`${i5Name}|2`];
    };
  }

  private adjustI5SeqLookup(colDef: InteractiveTableColDef, subSampleDef: SubSampleDefinition) {
    colDef.valueGetter = params => {
      const i5Name = params.getValue(`${subSampleDef.id}_${ColumnName.INDEX_2_NAME}`);
      const lookupData = get(params.colDef, 'data.lookup', {});
      return lookupData[`${i5Name}|2`];
    };
  }

  private mapGssFieldTypeToAgGrid(field: any): 'text' | 'dropdown' | 'checkbox' | 'multiselect' {
    if (field.type === 'select') {
      return isMultiSelectField(field) ? 'multiselect' : 'dropdown';
    } else {
      return 'text';
    }
  }

  /**
   * Checks if the sample data table has user data
   * @param rows sample data table rows
   */
  public isSampleDataTableEmpty(rows: ISampleDataRow[]): boolean {
    return isEmpty(rows)
      || !rows.find(row => Object.keys(row).some(field => (field !== 'index') && !isEmpty(row[field])));
  }

  /**
   * For context (BASE-84415), EdgeOS can run on instrument or from a computer.
   * When EdgeOS is loaded on instrument with no local file system, run planning should only
   *    allow downloading file to External Storage via Custom File Browser (CFB).
   * When EdgeOS is loaded on the computer for such instruments, run planning should give the option to
   *    allow downloading file to either External Storage or local file system.
   */
  public getDownloadTemplateOptions(isStandAlone: boolean, useInstrumentSpecificFileSystem: boolean, instrumentAgent: InstrumentAgentType)
    : DownloadTemplateOption[] {
    if (isStandAlone) { // NovaSeqXSeries onwards
      if (useInstrumentSpecificFileSystem) { // MiSeqi100Series onwards
        switch (instrumentAgent) {
          case InstrumentAgentType.COMPUTER:
            return [DownloadTemplateOption.EXTERNAL_STORAGE, DownloadTemplateOption.LOCAL_STORAGE];
          case InstrumentAgentType.INSTRUMENT:
            return [DownloadTemplateOption.EXTERNAL_STORAGE];
          default:
            return [DownloadTemplateOption.EXTERNAL_STORAGE, DownloadTemplateOption.LOCAL_STORAGE];
        }
      } else { // NovaSeqXSeries
        return [DownloadTemplateOption.LOCAL_STORAGE];
      }
    } else {
      return [DownloadTemplateOption.LOCAL_STORAGE];
    }
  }

  /**
   * For standalone mode, importSamplesOptions are determined the same way as getDownloadTemplateOptions.
   * For non-standalone mode:
   *   - Single-analysis instruments allow import of either CSV or sample sheet file.
   *   - Multi-analysis instruments only allow CSV file for now. However, we allow the CSV file to come from either local storage
   *     or from BSSH storage (e.g. for Descartes project)
   */
  public getImportSamplesOptions(isStandAlone: boolean, useInstrumentSpecificFileSystem: boolean, instrumentAgent: InstrumentAgentType,
    isMultiAnalysis: boolean): ImportSamplesOption[] {
    if (isStandAlone) { // NovaSeqXSeries onwards
      if (useInstrumentSpecificFileSystem) { // MiSeqi100Series onwards
        switch (instrumentAgent) {
          case InstrumentAgentType.COMPUTER:
            return [ImportSamplesOption.EXTERNAL_STORAGE, ImportSamplesOption.LOCAL_STORAGE];
          case InstrumentAgentType.INSTRUMENT:
            return [ImportSamplesOption.EXTERNAL_STORAGE];
          default:
            return [ImportSamplesOption.EXTERNAL_STORAGE, ImportSamplesOption.LOCAL_STORAGE];
        }
      } else { // NovaSeqXSeries
        return [ImportSamplesOption.LOCAL_STORAGE];
      }
    } else {
      return [ImportSamplesOption.LOCAL_STORAGE, ImportSamplesOption.BSSH];
    }
  }

  /**
   * Get all subsample fields from the combination of colDefFields and nonColDefFields
   * colDefFields = Fields that are to be displayed in sample data table after collapsing the subsamples
   * nonColDefFields = Fields that are NOT to displayed, but already exist in sampleData (when loading a run or sample sheet)
   *   -> Example: project field in standalone mode (project column is not displayed, but GSS will auto-generate project for every run) 
   */
  public getAllSubSampleFields(columnDefs: InteractiveTableColDef[], sampleDataRows: ISampleDataRow[], subSamplesConfig: SubSamplesConfiguration) {
    const allSampleDataFields = uniq(union(...sampleDataRows.map(row => Object.keys(row))));
    const subSampleDefIds = subSamplesConfig.subSampleDefinitions.map(x => x.id);
    const colDefFields = columnDefs.map(x => x.field);
    const nonColDefFields = []

    for (const field of allSampleDataFields) {
      const fieldsToCheck = [field, ...subSampleDefIds.map(id => `${id}_${field}`)];
      if (intersection(colDefFields, fieldsToCheck).length === 0) {
        nonColDefFields.push(field);
      }
    }

    // DO NOT include sampleName, otherwise it will break the collapse/expand logic
    // for all other nonColDefFields, it should be ok to just map
    const sampleNameIdx = nonColDefFields.findIndex(field => field === 'sampleName');
    if (sampleNameIdx !== -1) {
      nonColDefFields.splice(sampleNameIdx, 1);
    }

    return [...colDefFields, ...nonColDefFields];
  }

  /**
   * Map sampleDataRows from GSS payload to combined subSamples rows
   *
   * `columnDefinitions` (computed in previous rule) used as reference of column keys for subSample table
   * `sampleSettingsSnapshot` to identify assocFieldId(eg. Pair_ID) value from sampleName
   * `sampleData` original data from GSS
   * `sampleIdToSettingsMap` to add more data to `sampleData` from `sampleSettingsSnapshot`
   *
   * Refer to https://confluence.illumina.com/display/BS/Subsamples+in+Sample+Table for disambiguation of terms
  
   * E.g. sampleDate is 
   *    { "Sample_Name":"S1", "sampleName":"S1_DNA", "a":"1", "b":"1" },
        { "Sample_Name":"S1", "sampleName":"S1_DNA", "a":"2", "b":"2" },
        { "Sample_Name":"S1", "sampleName":"S1_RNA", "a":"1", "b":"1" },
        { "Sample_Name":"S2", "sampleName":"S2_DNA", "a":"3", "b":"3" }
      Returns:
      { "SampleName": "S1", "DNA_a": "1", "DNA_b": "1", "RNA_a": "1", "RNA_b": "1" },
      { "SampleName": "S1", "DNA_a": "2", "DNA_b": "2" },      
      { "SampleName": "S2", "DNA_a": "3", "DNA_b": "3" }
     
  */
  public collapseSubSamples(
    // Data sources:
    sampleData: ISampleDataRow[],
    // Data definitions:
    subSamplesConfig: SubSamplesConfiguration,
    allSubSampleFields: string[],
    sampleSettingsFieldMap: { [key: string]: FormValueObject },
    subSampleNameSeparator: string,
  ): ISampleDataRow[] {
    if (!sampleData || sampleData.length === 0) {
      return [];
    }

    if (!subSamplesConfig || !subSamplesConfig.associationFieldId) {
      // Nothing to collapse without association field; return original rows
      return sampleData;
    }

    const assocFieldId = subSamplesConfig.associationFieldId; // Eg: 'Pair_ID'

    const assocIdToSampleDataMap: { [key: string]: ISampleDataRow[] } = {};
    for (const row of sampleData) {
      if (!row || !row.sampleName) {
        // skip invalid rows
        continue;
      }
      /** Example row data being { "Sample_Name":"S1", "sampleName":"S1_DNA", "a":"1", "b":"1" }      
       * EXAMPLE assocId/sampleName on sample data table = 'S1'
       * EXAMPLE subSampleId = 'DNA'
       * EXAMPLE sampleName = 'S1_DNA'
       */
      const subsampleNameData = SubSampleNameUtility.parseSampleNameWithFieldId(row.sampleName,
        subSampleNameSeparator);
      const sampleName = subsampleNameData.sampleName; // Eg: "S1"
      const subSampleDefId = subsampleNameData.subSampleFieldId; // Eg: 'DNA', 'RNA', ...

      // Parse the data with subsample def ID as prefix
      const parsedData: { [key: string]: any } = allSubSampleFields
        .reduce((parsedRowData, field) => {
          if (field.startsWith(`${subSampleDefId}_`)) {
            /**
             * EXAMPLE field = 'rna_index1Sequence'
             * EXAMPLE subSampleId = 'rna'
             * EXAMPLE originalFieldName = 'index1Sequence'
             */
            const originalFieldName = field.replace(`${subSampleDefId}_`, '');
            if (row.hasOwnProperty(originalFieldName)) {
              // add prefix to maintain field's relation to its subsample
              parsedRowData[field] = row[originalFieldName];
            }
          } else if (Object.keys(sampleSettingsFieldMap).includes(field)) {
            const supportedSubSampleIds = get(sampleSettingsFieldMap[field], 'settings.supportedSubSampleIds', []);
            // if field is subsample-specific, add prefix to maintain field's relation to its subsample.
            // else, just map using original field name
            const destField = isEmpty(supportedSubSampleIds) ? field : `${subSampleDefId}_${field}`;
            parsedRowData[destField] = row[field];
          } else if (row[field] !== undefined) {
            // add prefix to maintain field's relation to its subsample
            const destField = `${subSampleDefId}_${field}`;
            parsedRowData[destField] = row[field];
          }
          return parsedRowData;
        }, {});

      // Initialize empty sample data row if not exist
      if (!assocIdToSampleDataMap[sampleName]) {
        const newSampleDataRow = {
          [assocFieldId]: sampleName, ...parsedData
        }
        assocIdToSampleDataMap[sampleName] = [newSampleDataRow];
      } else {
        // Find if there's a unpaired row based on the key
        const existingRows = assocIdToSampleDataMap[sampleName];
        const unpairedRow = existingRows.find(r =>  r[assocFieldId] == sampleName && 
            !Object.keys(r).some(x => x.startsWith(subSampleDefId + '_')));
        if (unpairedRow) {
          Object.assign(unpairedRow, parsedData);
          continue;
        } else {
          // If every row is paired already, add as a new row
          const newSampleDataRow = {
            [assocFieldId]: sampleName, ...parsedData
          }
          assocIdToSampleDataMap[sampleName].push(newSampleDataRow);
        }
      }
    }

    /**
     * Current use case: only one of the two arrays should have value
     * because only called on load planned run, import run or requeue, not at import samplesheet to append to existing table
     */
    return Object.values(assocIdToSampleDataMap).reduce((allRows, currRows) => { return allRows.concat(currRows) }, []);
  }

  private getColumnWidth(colDef: InteractiveTableColDef, fieldId): number {
    if (fieldId !== ColumnName.BARCODE_MISMATCH_READ_1 &&
      fieldId !== ColumnName.BARCODE_MISMATCH_READ_2) {
      // Other than the columns for barcode mismatch, set width based on header name length
      return colDef.headerName.length * 10;
    }
    return 200; // default width for barcode mismatch columns
  }
}
